import { useQueryClient } from "@tanstack/react-query";

import { getErrorMessage } from "@vega-api/custom/utils/errorHandling";
import { usePatchNotification } from "@vega-api/generated/notifications-v1/notifications-v1";
import { type ApiResponseUnreadCount } from "@vega-api/generated/types/apiResponseUnreadCount";
import { useToast } from "@vega-core/components/Toast/useToast";

import {
  type NotificationsListInfinite,
  updateAllPages,
} from "./notificationsCache";
import {
  getPublisherUnreadCountQueryKey,
  getScopedListQueryKey,
  invalidateAllNotifications,
  type NotificationScope,
} from "./notificationScope";

export const useNotificationsPatchMutation = (
  scope: NotificationScope = {},
) => {
  const queryClient = useQueryClient();
  const showToast = useToast();

  const listQueryKey = getScopedListQueryKey(scope);
  const unreadCountQueryKey = getPublisherUnreadCountQueryKey(scope);

  return usePatchNotification({
    mutation: {
      onMutate: async ({ id, data }) => {
        await Promise.all([
          queryClient.cancelQueries({ queryKey: listQueryKey }),
          queryClient.cancelQueries({ queryKey: unreadCountQueryKey }),
        ]);

        const previousList =
          queryClient.getQueryData<NotificationsListInfinite>(listQueryKey);
        const previousCount =
          queryClient.getQueryData<ApiResponseUnreadCount>(unreadCountQueryKey);

        if (data.read === true) {
          queryClient.setQueryData<NotificationsListInfinite>(
            listQueryKey,
            (current) =>
              updateAllPages(current, (entries) =>
                entries.map((item) =>
                  item.id === id && !item.readAt
                    ? { ...item, readAt: new Date().toISOString() }
                    : item,
                ),
              ),
          );

          queryClient.setQueryData<ApiResponseUnreadCount>(
            unreadCountQueryKey,
            (current) => {
              if (!current?.data) return current;

              return {
                ...current,
                data: { count: Math.max(0, current.data.count - 1) },
              };
            },
          );
        }

        if (data.dismissed === true) {
          queryClient.setQueryData<NotificationsListInfinite>(
            listQueryKey,
            (current) =>
              updateAllPages(current, (entries) =>
                entries.filter((item) => item.id !== id),
              ),
          );
        }

        return { previousList, previousCount };
      },
      onSuccess: async (response, { id, data }) => {
        const confirmed = response.data;

        await queryClient.cancelQueries({ queryKey: listQueryKey });

        const hasListCache = Boolean(
          queryClient.getQueryData<NotificationsListInfinite>(listQueryKey),
        );

        if (!hasListCache) {
          queryClient.invalidateQueries({ queryKey: listQueryKey });

          return;
        }

        queryClient.setQueryData<NotificationsListInfinite>(
          listQueryKey,
          (current) =>
            updateAllPages(current, (entries) => {
              if (data.dismissed === true) {
                return entries.filter((item) => item.id !== id);
              }

              if (data.read === true) {
                return entries.map((item) =>
                  item.id === id
                    ? {
                        ...item,
                        readAt:
                          confirmed.readAt ??
                          item.readAt ??
                          new Date().toISOString(),
                      }
                    : item,
                );
              }

              return entries;
            }),
        );
      },
      onSettled: () => {
        if (queryClient.isMutating() <= 1) {
          invalidateAllNotifications(queryClient);
        }
      },
      onError: (error, _vars, context) => {
        if (context?.previousList) {
          queryClient.setQueryData(listQueryKey, context.previousList);
        }

        if (context?.previousCount) {
          queryClient.setQueryData(unreadCountQueryKey, context.previousCount);
        }
        const { title, description } = getErrorMessage(error);
        showToast({ title, description, variant: "destructive" });
      },
    },
  });
};
